// Rewrites AI formula tokens ("#<Name>" → "#<id>", "[label:value]" → numeric literal) into the
// shape the scenario evaluator accepts: only /#\d+/ tokens, numeric literals and operators.

import { Variable } from 'src/api/types';
import { findVariableByToken } from 'src/components/FormulaInput/shared/formula';

/** The evaluator rejects a leading-dot number (".007"); ensure a leading zero ("0.007"). */
const ensureLeadingZero = (literal: string): string =>
  /^\s*\.\d/.test(literal) ? literal.replace(/^(\s*)\./, '$10.') : literal;

export const toScenarioFormula = (raw: string, variables: Variable[]): string => {
  let result = raw.replace(/\[[^\]]*:([^\]]+)\]/g, (_match, value: string) =>
    ensureLeadingZero(String(value).trim()),
  );

  result = result.replace(/#([A-Za-z0-9_]+)/g, (match, token: string) => {
    if (/^\d+$/.test(token)) return `#${token}`;
    const variable = findVariableByToken(variables, token);
    return variable ? `#${variable.id}` : match; // leave unresolved token as-is; surfaced upstream
  });

  return result;
};

/** Shunting-yard evaluator for `+ - * / ( )` over non-negative decimals (no unary minus). */
export const evaluateArithmetic = (expr: string): number => {
  const tokens = expr.match(/\d+\.?\d*|[+\-*/()]/g) ?? [];
  const values: number[] = [];
  const ops: string[] = [];
  const precedence: Record<string, number> = { '+': 1, '-': 1, '*': 2, '/': 2 };

  const apply = (): void => {
    const op = ops.pop();
    const b = values.pop();
    const a = values.pop();
    if (op === undefined || a === undefined || b === undefined) throw new Error('Malformed expression');
    values.push(op === '+' ? a + b : op === '-' ? a - b : op === '*' ? a * b : a / b);
  };

  tokens.forEach((token) => {
    if (/^\d/.test(token)) values.push(parseFloat(token));
    else if (token === '(') ops.push(token);
    else if (token === ')') {
      while (ops.length && ops[ops.length - 1] !== '(') apply();
      ops.pop();
    } else {
      while (ops.length && precedence[ops[ops.length - 1]] >= precedence[token]) apply();
      ops.push(token);
    }
  });
  while (ops.length) apply();

  return values[0];
};

/** Evaluate a formula to a £ amount; null when any referenced variable is unpopulated. */
export const computeFormulaValue = (
  raw: string,
  variables: { id: number; value: number | null }[],
): number | null => {
  const valueById = new Map(variables.map((v) => [v.id, v.value]));

  let missing = false;
  let expr = raw.replace(/#(\d+)/g, (_match, id: string) => {
    const value = valueById.get(Number(id));
    if (value == null) {
      missing = true;
      return '0';
    }
    return String(value);
  });
  if (missing) return null;

  expr = expr.replace(/\[[^\]]*:([^\]]+)\]/g, (_match, value: string) => ensureLeadingZero(String(value).trim()));

  try {
    const result = evaluateArithmetic(expr);
    return Number.isFinite(result) ? Math.round(result) : null;
  } catch {
    return null;
  }
};
